import type { TaskUpdate } from "@common/schemas/task.schema";
import {
    getEntityLockedTooltip,
    isEntityLockedTaskType,
    Task,
    TaskGroupBy,
    TaskPriority,
    TaskStatus,
} from "@common/types/task";
import {
    DueDateGroup,
    getDefaultDueDateForGroup,
    NO_ASSIGNEE_KEY,
    NO_CLOSE_STAGE_KEY,
    NO_TASK_GROUP_KEY,
} from "../taskListUtils";
import {
    ENTITY_LANE_BY,
    parseEntityLaneId,
    type TaskBoardLaneBy,
} from "./taskBoardLanes";

/** The fields a drop can change. */
export type TaskBoardMoveBody = Pick<
    TaskUpdate,
    | "status"
    | "priority"
    | "assigneeId"
    | "groupId"
    | "closeStageId"
    | "dueDate"
    | "entityIds"
>;

/** What a drop on one axis does: the fields it sets, or why it's refused. */
export type TaskAxisChange = { body: TaskBoardMoveBody } | { refusal: string };

const findValue = <T extends string>(values: readonly T[], id: string) =>
    values.find((value) => value === id);

const TASK_STATUSES = Object.values(TaskStatus);
const TASK_PRIORITIES = Object.values(TaskPriority);
const SETTABLE_DUE_DATE_GROUPS = [
    DueDateGroup.TODAY,
    DueDateGroup.TOMORROW,
    DueDateGroup.NO_DUE_DATE,
];

const orNull = (id: string, noneId: string) => (id === noneId ? null : id);

/** What landing in a column or lane sets, by grouping; null where it can't be dropped on. */
const GROUPING_CHANGES: Record<
    TaskGroupBy,
    (valueId: string) => TaskBoardMoveBody | null
> = {
    [TaskGroupBy.STATUS]: (valueId) => {
        const status = findValue(TASK_STATUSES, valueId);
        return status === undefined ? null : { status };
    },
    [TaskGroupBy.PRIORITY]: (valueId) => ({
        priority: findValue(TASK_PRIORITIES, valueId) ?? null,
    }),
    [TaskGroupBy.ASSIGNEE]: (valueId) => ({
        assigneeId: valueId === NO_ASSIGNEE_KEY ? null : Number(valueId),
    }),
    [TaskGroupBy.TASK_GROUP]: (valueId) => ({
        groupId: orNull(valueId, NO_TASK_GROUP_KEY),
    }),
    [TaskGroupBy.CLOSE_STAGE]: (valueId) => ({
        closeStageId: orNull(valueId, NO_CLOSE_STAGE_KEY),
    }),
    [TaskGroupBy.DUE_DATE]: (valueId) => {
        const group = findValue(SETTABLE_DUE_DATE_GROUPS, valueId);
        return group === undefined
            ? null
            : { dueDate: getDefaultDueDateForGroup(group) };
    },
    [TaskGroupBy.CLOSE]: () => null,
    [TaskGroupBy.CLIENT]: () => null,
};

const CROSS_CLIENT_REASON = "Tasks can't move between clients";

/** Why a value no single field update stands for refuses drops. */
const REFUSAL_REASONS: Partial<Record<TaskGroupBy, string>> = {
    [TaskGroupBy.DUE_DATE]: "Update the due date on the card",
    [TaskGroupBy.CLOSE]: "Update the service on the task",
    [TaskGroupBy.CLIENT]: CROSS_CLIENT_REASON,
};

/**
 * The entities after a copy moves from one entity lane to another: the
 * source entity swaps for the target one, the others stay. "No entity" as
 * the target only removes the source.
 */
const swapEntity = ({
    entityIds,
    fromId,
    toId,
}: {
    entityIds: readonly number[];
    fromId: string;
    toId: string;
}) => {
    const sourceId = parseEntityLaneId(fromId);
    const targetId = parseEntityLaneId(toId);
    const keptIds = entityIds.filter((entityId) => entityId !== sourceId);
    return targetId === null || keptIds.includes(targetId)
        ? keptIds
        : [...keptIds, targetId];
};

interface TaskAxisMove {
    task: Task;
    axis: TaskBoardLaneBy;
    fromId: string;
    toId: string;
    /** Each entity's client, so an entity lane can refuse another client's task. */
    entityWorkspaceIds: ReadonlyMap<number, string>;
    /** The organization page, where an entity lane refuses unless it's the task's client's. */
    isCrossWorkspace: boolean;
}

const getEntityChange = ({
    task,
    fromId,
    toId,
    entityWorkspaceIds,
    isCrossWorkspace,
}: Omit<TaskAxisMove, "axis">): TaskAxisChange => {
    if (isEntityLockedTaskType(task.type)) {
        return { refusal: getEntityLockedTooltip(task.type) };
    }
    const targetId = parseEntityLaneId(toId);
    if (
        isCrossWorkspace &&
        targetId !== null &&
        entityWorkspaceIds.get(targetId) !== task.workspaceId
    ) {
        return { refusal: CROSS_CLIENT_REASON };
    }
    return {
        body: {
            entityIds: swapEntity({
                entityIds: task.entityIds ?? [],
                fromId,
                toId,
            }),
        },
    };
};

/** What moving a card from one value of `axis` to another sets, or why it can't. */
export const getTaskAxisChange = (move: TaskAxisMove): TaskAxisChange => {
    const { axis, fromId, toId } = move;
    if (fromId === toId) {
        return { body: {} };
    }
    if (axis === ENTITY_LANE_BY) {
        return getEntityChange(move);
    }
    const body = GROUPING_CHANGES[axis](toId);
    const refusal = REFUSAL_REASONS[axis];
    if (body !== null) {
        return { body };
    }
    return refusal === undefined ? { body: {} } : { refusal };
};
